import { ICorporationResponse } from '@kcftech/prometheus-client';
import { useDebounce } from 'custom_hooks/useDebounce';
import { atom, createStore, useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
} from 'react';
import { createFuzzySearch, FuzzySearch } from 'shared/fuzzy_search/fuzzy_search';
import { HighlightRangePairs } from 'shared/fuzzy_search/render_search';
import { toMap } from 'utils/array';

type FuzzyCorpMatch = Readonly<{
    corpMatches: boolean;
    corpHighlight: HighlightRangePairs | null;
    matchingLocationIds: Set<string>;
    /** locationId -> highlight range pairs */
    locationHighlights: ReadonlyMap<string, HighlightRangePairs>;
}>;

type CorporationId = string;
type FuzzyCorpResults = Map<CorporationId, FuzzyCorpMatch>;

// ---

type LocEntry = Readonly<{ corpId: string; locationId: string; locationName: string }>;

function buildLocEntries(corporations: ICorporationResponse[]): LocEntry[] {
    const entries: LocEntry[] = [];
    for (const corp of corporations) {
        for (const loc of corp.Locations) {
            entries.push({
                corpId: corp.Id,
                locationId: loc.LocationId,
                locationName: loc.LocationName,
            });
        }
    }
    return entries;
}

/**
 * Runs fuzzy searchers against the corps/locs using Searchers, and returns
 * a map of CorporationId to { doesCorpMatch, matchingLocationIds }
 */
function computeFuzzyResults(
    corporations: ICorporationResponse[],
    filter: string,
    corpSearcher: FuzzySearch<ICorporationResponse>,
    locSearcher: FuzzySearch<LocEntry>,
): FuzzyCorpResults | null {
    if (!filter) {
        return null;
    }

    const corpMatches = corpSearcher.search(filter);
    const corpHighlightMap = new Map<string, HighlightRangePairs>();
    const matchingCorpIds = new Set<string>();
    for (const { item, ranges } of corpMatches) {
        matchingCorpIds.add(item.Id);
        corpHighlightMap.set(item.Id, ranges);
    }

    const locMatches = locSearcher.search(filter);
    const locMatchesByCorpId = new Map<string, Set<string>>();
    const locHighlightMap = new Map<string, HighlightRangePairs>();
    for (const { item, ranges } of locMatches) {
        let set = locMatchesByCorpId.get(item.corpId);
        if (!set) {
            set = new Set<string>();
            locMatchesByCorpId.set(item.corpId, set);
        }
        set.add(item.locationId);
        locHighlightMap.set(item.locationId, ranges);
    }

    const results: FuzzyCorpResults = new Map();
    for (const corp of corporations) {
        const isCorpMatch = matchingCorpIds.has(corp.Id);
        const matchingLocationIds = locMatchesByCorpId.get(corp.Id);
        if (isCorpMatch || matchingLocationIds) {
            results.set(corp.Id, {
                corpMatches: isCorpMatch,
                corpHighlight: corpHighlightMap.get(corp.Id) ?? null,
                matchingLocationIds: matchingLocationIds ?? new Set<string>(),
                locationHighlights: locHighlightMap,
            });
        }
    }

    return results;
}

// --- State & State Provider ---

const filterAtom = atom('');
const fuzzyResultsAtom = atom<FuzzyCorpResults | null>(null);
const isFilterActiveAtom = atom((get) => get(fuzzyResultsAtom) !== null);
const locIdToCorpIdAtom = atom(new Map<string, string>());

type CorpSearchStore = ReturnType<typeof createStore>;

const CorpSearchStoreCtx = createContext<CorpSearchStore | null>(null);

function useSearchStore(): CorpSearchStore {
    const store = useContext(CorpSearchStoreCtx);
    if (!store) {
        throw new Error('useCorpSearch* must be used inside <CorpSearchProvider>');
    }
    return store;
}

type CorpSearchProviderProps = Readonly<{
    corporations: ICorporationResponse[] | undefined;
    children: ReactNode;
}>;

export function CorpSearchProvider({ corporations, children }: CorpSearchProviderProps) {
    const store = useMemo(() => createStore(), []);
    const setResults = useSetAtom(fuzzyResultsAtom, { store });
    const filter = useAtomValue(filterAtom, { store });
    const setLocIdToCorpId = useSetAtom(locIdToCorpIdAtom, { store });

    const searchers = useMemo(() => {
        if (!corporations) {
            return null;
        }
        const locEntries = buildLocEntries(corporations);
        setLocIdToCorpId(
            toMap(
                locEntries,
                (e) => e.locationId,
                (e) => e.corpId,
            ),
        );
        return {
            corp: createFuzzySearch(corporations, (c) => c.Name),
            loc: createFuzzySearch(locEntries, (e) => e.locationName),
        };
    }, [corporations, setLocIdToCorpId]);

    const debouncedFilter = useDebounce(filter, 250);
    useEffect(() => {
        const results =
            corporations && searchers
                ? computeFuzzyResults(
                      corporations,
                      debouncedFilter,
                      searchers.corp,
                      searchers.loc,
                  )
                : null;
        setResults(results);
    }, [corporations, searchers, debouncedFilter, setResults]);

    return (
        <CorpSearchStoreCtx.Provider value={store}>
            {children}
        </CorpSearchStoreCtx.Provider>
    );
}

export function useCorpSearchFilter() {
    const store = useSearchStore();
    const filter = useAtomValue(filterAtom, { store });
    const setFilter = useSetAtom(filterAtom, { store });
    return { filter, setFilter };
}

// --- State Access ---

export function useCorpSearchFilterActive(): boolean {
    const store = useSearchStore();
    return useAtomValue(isFilterActiveAtom, { store });
}

export type CorpSearchInfo = Readonly<{
    visible: boolean;
    highlight: HighlightRangePairs | null;
    /** Location ids to show when only child locations matched (not the corp itself) */
    matchingLocationIds: Set<string> | null;
}>;

const NO_FILTER: CorpSearchInfo = {
    visible: true,
    highlight: null,
    matchingLocationIds: null,
};

export function useCorpSearchInfo(corpId: string): CorpSearchInfo {
    const store = useSearchStore();
    const selector = useCallback(
        (results: FuzzyCorpResults | null): CorpSearchInfo => {
            if (!results) {
                return NO_FILTER;
            }
            const match = results.get(corpId);
            if (!match) {
                return { visible: false, highlight: null, matchingLocationIds: null };
            }
            return {
                visible: true,
                highlight: match.corpHighlight,
                matchingLocationIds: match.corpMatches ? null : match.matchingLocationIds,
            };
        },
        [corpId],
    );
    return useAtomValue(selectAtom(fuzzyResultsAtom, selector), { store });
}

export function useLocationHighlight(locationId: string): HighlightRangePairs | null {
    const store = useSearchStore();
    const selector = useCallback(
        (
            results: FuzzyCorpResults | null,
            locIdToCorpId: Map<string, string>,
        ): HighlightRangePairs | null => {
            if (!results) {
                return null;
            }
            const corpId = locIdToCorpId.get(locationId);
            if (!corpId) {
                return null;
            }
            const match = results.get(corpId)?.locationHighlights.get(locationId);
            if (!match) {
                return null;
            }
            return match;
        },
        [locationId],
    );
    const highlightAtom = atom((get) => {
        const results = get(fuzzyResultsAtom);
        const locIdToCorpId = get(locIdToCorpIdAtom);

        return selector(results, locIdToCorpId);
    });
    return useAtomValue(highlightAtom, { store });
}
